import { useEffect, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { ModelConfig } from '../types/models';
import { loadModelConfig, createConfigFromGlbUrl } from '../services/configLoader';

export function useModelConfig(modelKey: string, customGlbUrl: string | null, enabled = true) {
  const [modelConfig, setModelConfig] = useState<ModelConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setModelConfig(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setModelConfig(null);

    const loadConfig = async () => {
      let config: ModelConfig | null = null;

      if (customGlbUrl) {
        try {
          const isBlobUrl = customGlbUrl.startsWith('blob:');
          if (!isBlobUrl) {
            const headResponse = await fetch(customGlbUrl, { method: 'HEAD' });
            if (!headResponse.ok) {
              throw new Error(`HTTP ${headResponse.status}: ${headResponse.statusText}`);
            }
          }
          config = createConfigFromGlbUrl(customGlbUrl);
        } catch (err) {
          const errorMsg = `Failed to load GLB from URL: ${err instanceof Error ? err.message : String(err)}`;
          console.error(errorMsg);
          setError(errorMsg);
          setLoading(false);
          return;
        }
      } else {
        config = await loadModelConfig(modelKey);
      }

      if (config) {
        setModelConfig(config);
        if (config.modelPath.startsWith('/')) {
          useGLTF.preload(config.modelPath);
        }
      } else {
        const errorMsg = `Model config not found for "${modelKey}"`;
        console.error(errorMsg);
        setError(errorMsg);
      }
      setLoading(false);
    };

    loadConfig();
  }, [customGlbUrl, enabled, modelKey]);

  return { modelConfig, loading, error };
}
